import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    AudioPlayer,
    BulletList,
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay1,
    LabelDisplay2,
    LabelTelescope,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import { IllustrationBulb, IllustrationWeightLifter } from '../../icons';
import { ExerciseDto, ExerciseStatus, Question } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { ExerciseViewProps } from '../exercises';

const page1InputKey = 'page1Input';
const page3InputKey = 'page3Input';

export const dataKeys = [page1InputKey, page3InputKey];
export const questions: Question[] = [
    createQuestionFromConfiguration({
        title: 'Hvordan var det?',
        description: (
            <BulletList
                items={[
                    'Hvor meget kæmpede du imod? Prøv at tænke på det som en skala fra 0-5, hvor 5 er at du brugte mange kræfter.',
                    'Hvad mærkede du undervejs?',
                    'Var der øjeblikke, hvor det lykkedes?',
                ]}
            />
        ),
        dataKeys: [page1InputKey],
    }),
    createQuestionFromConfiguration({
        title: 'Skriv din målsætning',
        description: (
            <>
                At gøre det konkret er en hjælp til dig selv og øger chancen
                for, at du husker det. Det kan være som dette eksempel:
                <br />
                Når jeg er færdig med aftensmaden
                <br />
                ...vil jeg øve mig i at være med ubehag på en ny måde.
            </>
        ),
        dataKeys: [page3InputKey],
    }),
];

export function Module1S1Exercise3({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    return (
        <Exercise title="Se ubehaget i øjnene">
            <Page />
        </Exercise>
    );

    function Page() {
        switch (exercise.CurrentPage) {
            case 3:
                return (
                    <Page3
                        savedInput={exercise.Data[page3InputKey] ?? ''}
                        onSave={(input) => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                                data: {
                                    [page3InputKey]: normalizeInput(input),
                                },
                            });
                        }}
                        onSkipSaving={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                                data: { [page3InputKey]: null },
                            });
                        }}
                    />
                );
            case 2:
                return (
                    <Page2
                        onFinish={() => {
                            onContinue({
                                status: ExerciseStatus.Completed,
                                goToNext: false,
                            });
                        }}
                        onContinue={() => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 3,
                            });
                        }}
                    />
                );
            case 1:
            default:
                return (
                    <Page1
                        exercise={exercise}
                        savedInput={exercise.Data[page1InputKey] ?? ''}
                        onContinue={(input) => {
                            onContinue({
                                status: ExerciseStatus.InProgress,
                                pageNumber: 2,
                                data: {
                                    [page1InputKey]: normalizeInput(input),
                                },
                            });
                        }}
                    />
                );
        }
    }
}

type Page1Props = {
    exercise: ExerciseDto;
    savedInput: string;
    onContinue: (input: string) => void;
};
function Page1({ exercise, savedInput, onContinue }: Page1Props) {
    const [input, setInput] = useSyncedState(savedInput);
    return (
        <>
            <LabelDisplay2>
                Bliv guidet i at være med det der fylder
            </LabelDisplay2>
            <LabelBody class="mb-30 mt-10">
                Her er en mindfulnessøvelse, der guider og støtter dig i at være
                nysgerrig på ubehag. Find et roligt sted og tryk afspil, når du
                er klar.
            </LabelBody>
            <AudioPlayer
                exercise={exercise}
                duration="00:08:10"
                src="https://static.hejdoktor.dk/falcksundtsind/SE+UBEHAGET+I+ØJNENE+02+MIX.mp3"
                type="audio/mpeg"
            >
                <LabelBodySmall>Mindfulness</LabelBodySmall>
                <LabelBodyHeadline>Se ubehaget i øjnene</LabelBodyHeadline>
            </AudioPlayer>
            <SwirlyBox class="my-30">
                Hvordan var det?
                <br />
                Prøv at mærke efter.
            </SwirlyBox>
            <Card>
                <LabelDisplay1>Spørgsmål</LabelDisplay1>
                <LabelBody className="mt-10">
                    <BulletList
                        items={[
                            'Hvad mærkede du undervejs?',
                            'Var der tidspunkter, hvor du kæmpede imod?',
                            'Var der øjeblikke, hvor det lykkedes?',
                        ]}
                    />
                </LabelBody>
                <Textarea text={input} onChangeText={setInput} />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={() => onContinue(input)}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type Page2Props = {
    onContinue: () => void;
    onFinish: () => void;
};
function Page2({ onContinue, onFinish }: Page2Props) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Et godt redskab</LabelDisplay2>
            <LabelBody>
                Mindfulness kan hjælpe dig med at give plads til, at ubehagelige
                tanker og følelser er der, på en måde så de ikke bliver
                overvældende og fylder det hele.{' '}
            </LabelBody>
            <IllustrationBulb className="mx-auto my-20" />

            <LabelDisplay2 class="mb-10">Mod på mere?</LabelDisplay2>
            <LabelBody>
                Det er godt at øve sig et par gange i hverdagen. Hvis du kunne
                lide lydklippet, kan du sætte dig et mål om at øve dig i at
                tillade ubehaget.
            </LabelBody>
            <Spacer class="mt-30" />
            <SecondaryButton onClick={onContinue} class="mb-20">
                Fortsæt
            </SecondaryButton>
            <GhostButton onClick={onFinish}>Afslut øvelsen</GhostButton>
        </>
    );
}

type Page3Props = {
    savedInput: string;
    onSave: (input: string) => void;
    onSkipSaving: () => void;
};
function Page3({ savedInput, onSave, onSkipSaving }: Page3Props) {
    const [input, setInput] = useSyncedState(savedInput);
    return (
        <>
            <LabelDisplay2 class="mb-10">Sæt dig et mål</LabelDisplay2>
            <LabelBody>
                Over tid vil det at observere og bare beskrive dit ubehag blive
                et redskab, du kan bruge, hvis du oplever dig overvældet af
                svære tanker, følelser og fornemmelser.
                <br />
                <br />
                Hvis du vil eksperimentere med denne øvelse de næste par dage,
                kan du vælge et specifikt tidspunkt og sted, du vil
                eksperimentere f.eks. om morgenen i din sofa.
            </LabelBody>
            <IllustrationWeightLifter className="mx-auto my-30" />
            <Card>
                <LabelDisplay1 class="mb-10">
                    Skriv din målsætning
                </LabelDisplay1>
                <LabelBody>
                    At gøre det konkret er en hjælp til dig selv og øger chancen
                    for, at du husker det. Det kan være hvor eller hvornår, du
                    vil gøre det.
                </LabelBody>
                <LabelTelescope class="mt-10">
                    Når jeg er færdig med aftensmaden, vil jeg lytte til
                    øvelsen.
                </LabelTelescope>
                <Textarea text={input} onChangeText={setInput} />
                <LabelBody class="mt-10">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={() => onSave(input)} class="mt-30">
                Gem i logbogen
            </SecondaryButton>
            {!savedInput && (
                <GhostButton onClick={onSkipSaving} class="mt-20">
                    Spring over
                </GhostButton>
            )}
        </>
    );
}
